import reviewsData from "../data/reviews.json";
import { Review, ReviewMetric, ReviewsDataset, ReviewSummary } from "../Model/Review.model";

const reviewDataset = reviewsData as ReviewsDataset;

const ratingLabels: Record<string, string> = {
  "5": "Excellent",
  "4": "Great",
  "3": "Average",
  "2": "Poor",
  "1": "Bad",
};

function sortReviewsByDate(reviews: Review[]) {
  return [...reviews].sort((leftReview, rightReview) => {
    const leftDate = new Date(leftReview.datedOn).getTime();
    const rightDate = new Date(rightReview.datedOn).getTime();
    return rightDate - leftDate;
  });
}

function getLinkedReviews(
  predicate: (review: Review) => boolean,
  limit = 3,
) {
  const matches = sortReviewsByDate(
    reviewDataset.reviews.filter(predicate),
  );

  if (matches.length) {
    return matches.slice(0, limit);
  }

  return getFeaturedReviews(limit);
}

export function formatReviewCount(count: number) {
  return new Intl.NumberFormat("en-GB").format(count);
}

export function getReviewSummary(): ReviewSummary {
  return reviewDataset.summary;
}

export function getReviewMetrics(): ReviewMetric[] {
  return reviewDataset.metrics;
}

export function getRatingDistribution() {
  const totalReviews = reviewDataset.summary.totalReviews || 1;

  return Object.entries(reviewDataset.summary.distribution)
    .sort((leftEntry, rightEntry) => Number(rightEntry[0]) - Number(leftEntry[0]))
    .map(([stars, count]) => ({
      stars: Number(stars),
      label: ratingLabels[stars] || `${stars} stars`,
      count,
      percentage: Math.round((count / totalReviews) * 1000) / 10,
    }));
}

export function getAllReviews() {
  return sortReviewsByDate(reviewDataset.reviews);
}

export function getFeaturedReviews(limit = 6) {
  const featuredReviews = sortReviewsByDate(
    reviewDataset.reviews.filter((review) => review.featured),
  );

  if (featuredReviews.length >= limit) {
    return featuredReviews.slice(0, limit);
  }

  const fallbackReviews = sortReviewsByDate(reviewDataset.reviews).filter(
    (review) => !review.featured,
  );

  return [...featuredReviews, ...fallbackReviews].slice(0, limit);
}

export function getReviewsByServiceSlug(serviceSlug?: string, limit = 3) {
  if (!serviceSlug) {
    return getFeaturedReviews(limit);
  }

  return getLinkedReviews(
    (review) => review.serviceSlugs?.includes(serviceSlug) || false,
    limit,
  );
}

export function getReviewsByAssignmentSlug(assignmentSlug?: string, limit = 3) {
  if (!assignmentSlug) {
    return getFeaturedReviews(limit);
  }

  return getLinkedReviews(
    (review) => review.assignmentSlugs?.includes(assignmentSlug) || false,
    limit,
  );
}

export function getReviewsByBlogSlug(blogSlug?: string, limit = 3) {
  if (!blogSlug) {
    return getFeaturedReviews(limit);
  }

  return getLinkedReviews(
    (review) => review.blogSlugs?.includes(blogSlug) || false,
    limit,
  );
}

export function getReviewsByCategory(category?: string, limit = 6) {
  if (!category) {
    return getFeaturedReviews(limit);
  }

  return getLinkedReviews(
    (review) => review.category === category,
    limit,
  );
}

export function getReviewCategories() {
  return Array.from(
    new Set(reviewDataset.reviews.map((review) => review.category)),
  );
}

export function getReviewHeadline() {
  const summary = getReviewSummary();

  return `${summary.averageRating.toFixed(1)}/5 from ${formatReviewCount(
    summary.totalReviews,
  )} verified student reviews`;
}
